import { useMemo } from 'react';
import {
  ReportType,
  ReportShapeItem,
  ShapeType,
  FilterType,
} from '../../../reportsTypes';
import { ShapeDictionaryType } from '../../../utils';
import { GeoJson } from '@ais-ui/api-spec/shapes';

const asIdString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? undefined : String(value);
  }

  return undefined;
};

interface Params {
  reportType: ReportType;
  reportTemplateName: string | undefined;
  firstReportName: string | undefined;
  dateRange: { from: Date; to: Date } | undefined;
  email: string[] | undefined;
  phone: string | undefined;
  countryCode: string | undefined;
  selectedGroups: unknown[] | undefined;
  miscellaneous: Record<string, unknown> | undefined;
  shapeDictionary: ShapeDictionaryType | null;
  filter: unknown;
  ships: unknown[] | undefined;
  shipAttribute: 'colorBy' | 'filterBy' | undefined;
  currentShapeItems: ReportShapeItem[] | undefined;
  shapeType: ShapeType;
  filterType: FilterType;
  instantShape?: boolean;
}

function extractIdLike(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;

  const propertyCandidate = (() => {
    const properties = record['properties'];
    if (!properties || typeof properties !== 'object') return undefined;
    const propRecord = properties as Record<string, unknown>;
    return (
      asIdString(propRecord['id']) ||
      asIdString(propRecord['uuid']) ||
      asIdString(propRecord['geoJsonId'])
    );
  })();

  const directKeys: Array<string> = [
    'id',
    'uuid',
    'geoJsonId',
    'shapeId',
    'filterId',
    'ID',
    'UUID',
    'geoJsonID',
    'shape_id',
    'geojson_id',
  ];

  const directCandidate = directKeys.reduce<string | undefined>((memo, key) => {
    if (memo) return memo;
    const valueAtKey = record[key];
    return asIdString(valueAtKey);
  }, undefined);

  return directCandidate ?? propertyCandidate;
}

function extractIdsFromFeatureCollection(value: unknown): string[] {
  if (!value || typeof value !== 'object') {
    return [];
  }

  const record = value as Record<string, unknown>;
  const features = Array.isArray(record['features'])
    ? (record['features'] as GeoJson[])
    : [];

  const ids = features
    .map((feature) => {
      const rawProperties = feature?.properties;
      if (!rawProperties || typeof rawProperties !== 'object') {
        return undefined;
      }
      const properties = rawProperties as Record<string, unknown>;
      const id = asIdString(properties['id']);
      return id;
    })
    .filter((id): id is string => typeof id === 'string');

  return ids;
}

export function useQuickCreateInitialValues(params: Params) {
  const {
    reportType,
    reportTemplateName,
    firstReportName,
    dateRange,
    email,
    phone,
    countryCode,
    selectedGroups,
    miscellaneous,
    shapeDictionary,
    filter,
    ships,
    shipAttribute,
    currentShapeItems,
    shapeType,
    filterType,
    instantShape = false,
  } = params;

  return useMemo(() => {
    const startDateTime =
      dateRange?.from || new Date(Date.now() - 24 * 60 * 60 * 1000);
    const endDateTime = dateRange?.to || new Date();

    const values: Record<string, unknown> = {
      report_template_name: reportTemplateName,
      report_name: firstReportName,
      start_datetime: startDateTime,
      end_datetime: endDateTime,
      miscellaneous: {
        selectedGroups: selectedGroups || [],
        email: email || [],
        phone: phone || '',
        countryCode: countryCode || '',
        ...miscellaneous,
      },
    };

    if (reportType.shape && currentShapeItems) {
      currentShapeItems.forEach((shapeItem) => {
        const dictionaryRecord = (shapeDictionary ?? {}) as Record<
          string,
          unknown
        >;
        const contextVal = dictionaryRecord[shapeItem.name];

        if (contextVal == null) {
          if (shapeType === 'multiShape' || shapeType === 'ports')
            values[shapeItem.name] = [];
          else if (shapeType === 'worldShape') values[shapeItem.name] = false;
          else values[shapeItem.name] = instantShape ? null : '';
          return;
        }
        if (Array.isArray(contextVal)) {
          values[shapeItem.name] = contextVal.map((entry) =>
            typeof entry === 'number' ? String(entry) : entry,
          );
          return;
        }

        if (typeof contextVal === 'object') {
          // For instant shapes, pass the Geometry object directly
          if (instantShape) {
            // Check if it's a GeoJSON Feature - extract the geometry
            if ('geometry' in contextVal && contextVal.geometry) {
              values[shapeItem.name] = contextVal.geometry;
              return;
            }
            // Otherwise pass the object as-is (it's likely already a Geometry)
            values[shapeItem.name] = contextVal;
            return;
          }

          // For regular shapes (not instant), extract IDs
          // Check if it's a FeatureCollection (has features array)
          const hasFeatures =
            'features' in contextVal && Array.isArray(contextVal.features);

          if (hasFeatures) {
            // FeatureCollection - extract IDs for multiShape/ports
            const ids = extractIdsFromFeatureCollection(contextVal);
            if (shapeType === 'multiShape' || shapeType === 'ports') {
              values[shapeItem.name] = ids;
            } else {
              // For single shape, take the first ID
              values[shapeItem.name] = ids[0] || '';
            }
            return;
          }

          // Single Feature (not FeatureCollection) - extract ID
          const id = extractIdLike(contextVal);
          if (id) {
            values[shapeItem.name] = id;
            return;
          }
        }

        if (typeof contextVal === 'string' || typeof contextVal === 'number') {
          const idString = asIdString(contextVal);
          values[shapeItem.name] = idString ?? '';
          return;
        }

        // Fallback
        values[shapeItem.name] = '';
      });
    }

    if (reportType.filter) {
      if (filter) {
        if (filterType === 'normal' || filterType === 'shiplist') {
          if (typeof filter === 'string' || typeof filter === 'number') {
            values.filter = asIdString(filter) ?? '';
          } else if (typeof filter === 'object') {
            const baseObj = filter as Record<string, unknown>;
            const directId = extractIdLike(baseObj);
            const fallback = baseObj['filter'];
            const fallbackId = asIdString(fallback);
            values.filter = directId || fallbackId || '';
          } else {
            values.filter = '';
          }
        } else if (filterType === 'ship') {
          // Ship filter should always be an array of AutoCompleteShipInfo
          // Prioritize filter context over ships context since filter is specifically
          // for the Filter step, while ships is for the Ships step
          if (Array.isArray(filter)) {
            values.filter = filter;
          } else if (Array.isArray(ships) && ships.length > 0) {
            values.filter = ships;
          } else if (typeof filter === 'object' && filter !== null) {
            // Check if it's wrapped in a 'filter' property
            const arr = (filter as Record<string, unknown>)['filter'];
            values.filter = Array.isArray(arr) ? arr : [];
          } else {
            values.filter = [];
          }
        } else if (filterType === 'combined') {
          if (typeof filter === 'object' && filter !== null) {
            const obj = filter as Record<string, unknown> & {
              shiplist?: unknown;
              uuid?: unknown;
              id?: unknown;
              filter?: unknown;
            };
            values.shiplist =
              typeof obj.shiplist === 'string' ? obj.shiplist : '';
            const combinedIdCandidates = [obj.uuid, obj.id, obj.filter]
              .map((candidate) => asIdString(candidate))
              .find((candidate): candidate is string => !!candidate);
            values.filter = combinedIdCandidates || '';
          } else {
            values.shiplist = '';
            values.filter = '';
          }
        }
      } else {
        // defaults when filter type exists but no context filter
        if (filterType === 'normal' || filterType === 'shiplist')
          values.filter = '';
        else if (filterType === 'ship') values.filter = [];
        else if (filterType === 'combined') {
          values.shiplist = '';
          values.filter = '';
        }
      }
    }

    // Handle ships configuration (separate from filter)
    if (reportType.ships) {
      // Set ships array
      if (Array.isArray(ships) && ships.length > 0) {
        values.ships = ships;
      } else {
        values.ships = [];
      }

      // Set shipAttribute with default fallback
      if (shipAttribute) {
        values.shipAttribute = shipAttribute;
      } else {
        // Default to filterBy if enabled, otherwise colorBy
        if (reportType.ships.filterBy) {
          values.shipAttribute = 'filterBy';
        } else if (reportType.ships.colorBy) {
          values.shipAttribute = 'colorBy';
        }
      }
    }

    return values;
  }, [
    reportType,
    reportTemplateName,
    firstReportName,
    dateRange,
    email,
    phone,
    countryCode,
    selectedGroups,
    miscellaneous,
    shapeDictionary,
    filter,
    ships,
    shipAttribute,
    currentShapeItems,
    shapeType,
    filterType,
    instantShape,
  ]);
}
